import { useQuery } from '@tanstack/react-query'
import { preferencesApi } from './api'
import { qk } from '@/lib/queryKeys'

/**
 * GET /api/me — org + role + resolved preferences + orgs list. This is the
 * query `usePreferences()` (src/hooks/usePreferences.ts) reads for its
 * app-wide formatters, and the one `orgStore.bootstrap()` seeds at app start
 * so the first mount here doesn't duplicate that request (plan 023 step 7,
 * design decision 9). Also the query `RequireOrg`/`GuestRoute` read to decide
 * whether the caller has an organization (plan 024 / ADR-0009).
 *
 * ``enabled`` defaults to true; pass false to skip the fetch entirely (e.g.
 * GuestRoute must not fire this while the visitor is unauthenticated — an
 * unauthenticated /api/me would 401, which the shared http client turns into
 * a doomed refresh-token attempt and an `Authentication expired` throw).
 */
export function useIdentityQuery(opts?: { enabled?: boolean }) {
  return useQuery({
    queryKey: qk.identity(),
    queryFn: () => preferencesApi.getIdentity(),
    enabled: opts?.enabled ?? true,
  })
}

/**
 * GET /api/me/preferences — both layers (effective + the caller's raw
 * overrides + the org's own values), so the Settings UI can render
 * inheritance ("Following your organisation — 08:00-17:00"). `useIdentityQuery`
 * only carries the resolved `effective` set, not the breakdown.
 */
export function usePreferencesQuery() {
  return useQuery({
    queryKey: qk.preferences(),
    queryFn: () => preferencesApi.getPreferences(),
  })
}
